import {useMutation} from "@tanstack/react-query";
import {emailDuplicity, sendOTP} from "api/auth.api.ts";
import {useCreateAccount} from "context/CreateAccountContext.jsx";
import {useToast} from "hooks";
import {useEffect, useState} from "react";
import styled from "styled-components";
import {AppButton} from "ui/base/appButton/AppButton.jsx";
import {AppButtonAuth} from "ui/base/appButton/AppButtonAuth.jsx";
import {HelperText} from "ui/base/helperText/HelperText";
import {InputSignUp} from "ui/base/InputSignUp/InputSignUp";
import {EmailIcon, LockIcon, PhoneIcon, ProfileIcon} from "ui/icons/index.js";
import {emailValidation, formatPhoneNumber, passwordValidation, phoneNumberValidation,} from "utils";
import {pixelTrackEvent} from "utils/facebookPixel.js";
import {mediaSizes, themeColor} from "../../../theme";

const MSG_PASSWORD_VALIDATION =
    "Password must be a combination of 6-20 characters with at least 1 upper case, 1 lower case, 1 numeric and 1 special character. Spaces are not allowed.";

export const Signup = ({setActiveTab, setLoginModal, handleClose}) => {
    const {formData, updateFormData} = useCreateAccount();

    const [name, setName] = useState("");
    const [phoneLength, setPhoneLength] = useState(10);
    const [errors, setErrors] = useState({});

    const splitFullName = (fullName = "") => {
        const trimmed = fullName.trim();
        if (!trimmed) return {firstName: "", lastName: ""};
        const parts = trimmed.split(/\s+/);
        if (parts.length === 1) return {firstName: parts[0], lastName: parts[0]};
        return {firstName: parts[0], lastName: parts.slice(1).join(" ")};
    };

    const isNextButtonDisabled = !(
        name &&
        formData.email &&
        formData.phone &&
        formData.password &&
        formData.confirmPassword
    );

    const validateForm = (localData) => {
        const newErrors = {};

        if (!name.trim()) {
            newErrors.name = {value: true, text: "Enter your name"};
        }

        if (!emailValidation(localData.email)) {
            newErrors.email = {
                value: true,
                text: localData.email ? "Enter correct email" : "Email must be filled",
            };
        }

        if (localData.phone.length !== 12 && localData.phone.length !== 15) {
            newErrors.phone = {value: true, text: "Enter correct phone number"};
        }

        if (localData.password !== localData.confirmPassword) {
            newErrors.password = {value: true, text: "Password should match"};
            newErrors.confirmPassword = {value: true, text: "Password should match"};
        } else if (!passwordValidation(localData.password)) {
            newErrors.password = {value: true, text: MSG_PASSWORD_VALIDATION};
            newErrors.confirmPassword = {value: true, text: MSG_PASSWORD_VALIDATION};
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const changeFormData = (key, value) => {
        if (key === "phone") {
            const validated = phoneNumberValidation(value);
            updateFormData("phone", formatPhoneNumber(validated));
            value?.startsWith("+1") ? setPhoneLength(15) : setPhoneLength(12);
            return;
        }

        if (key === "password" || key === "confirmPassword") {
            setErrors((prev) => ({
                ...prev,
                password: {value: false, text: ""},
                confirmPassword: {value: false, text: ""},
            }));
        }

        updateFormData(key, value);
    };

    const {mutate: requestOTP, isLoading: requestOTPLoading} = useMutation({
        mutationFn: sendOTP,
        onSuccess: () => setActiveTab("otp"),
        onError: (err) => {
            useToast("error", err?.response?.data?.message || "Error requesting OTP");
        },
    });

    const {mutate: checkEmail, isLoading: checkEmailLoading} = useMutation({
        mutationFn: emailDuplicity,
        onSuccess: () => requestOTP({
            country_code: "+1",
            phone_number:formData.phone.replaceAll("-",''),
            new_signup: true,
            // email:formData.email,
        }),
        onError: () => {
            setErrors((prev) => ({
                ...prev,
                email: {value: true, text: "Email already exists"},
            }));
        },
    });

    const onSubmit = () => {
        pixelTrackEvent("Create-account: Clicked on Next");

        const isValid = validateForm(formData);
        if (!isValid) return;

        const {firstName, lastName} = splitFullName(name);
        updateFormData("firstName", firstName);
        updateFormData("lastName", lastName);

        checkEmail(formData.email.toLowerCase());
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") onSubmit();
    };

    useEffect(() => {
        pixelTrackEvent("Enter Create account page");
    }, []);

    return (
        <CreateAccountContainer className="form-card">
            <TitleText center centerMobile>
                Create Account
            </TitleText>

            <SubtitleText size="regular" center centerMobile weight={400}>
                Already have an account?{" "}
                <HelperText
                    color="blue"
                    center
                    style={{cursor: "pointer"}}
                    onClick={() => {
                        pixelTrackEvent("Create-account: Clicked on Login");
                        handleClose();
                        setLoginModal(true);
                    }}
                >
                    Login
                </HelperText>
            </SubtitleText>

            <CreateAccountRow>
                <InputSignUp
                    id="name"
                    value={name}
                    setValue={(value) => setName(value)}
                    placeholder="Full Name"
                    icon={<ProfileIcon/>}
                    onKeyDown={handleKeyDown}
                    maxLength={40}
                    isError={errors?.name?.value}
                    errorText={errors?.name?.text}
                />
            </CreateAccountRow>

            <CreateAccountRow>
                <InputSignUp
                    id="email"
                    value={formData.email}
                    placeholder="Email"
                    icon={<EmailIcon/>}
                    onKeyDown={handleKeyDown}
                    setValue={(input) => changeFormData("email", input.trim())}
                    isError={errors?.email?.value}
                    errorText={errors?.email?.text}
                />

                <InputSignUp
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    placeholder="Phone"
                    icon={<PhoneIcon/>}
                    onKeyDown={handleKeyDown}
                    setValue={(input) => changeFormData("phone", input)}
                    maxLength={phoneLength}
                    onBlur={() => {
                        if (
                            formData.phone &&
                            formData.phone.length !== 12 &&
                            formData.phone.length !== 15
                        ) {
                            setErrors((prev) => ({
                                ...prev,
                                phone: {
                                    value: true,
                                    text: "Enter correct phone number",
                                },
                            }));
                        }
                    }}
                    isError={errors?.phone?.value}
                    errorText={errors?.phone?.text}
                />
            </CreateAccountRow>

            <CreateAccountRow>
                <InputSignUp
                    id="password"
                    value={formData.password}
                    type="password"
                    placeholder="Password"
                    onKeyDown={handleKeyDown}
                    icon={<LockIcon/>}
                    setValue={(input) => changeFormData("password", input)}
                    maxLength={20}
                    isError={errors?.password?.value}
                    errorText={errors?.password?.text}
                />

                <InputSignUp
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    type="password"
                    placeholder="Confirm Password"
                    onKeyDown={handleKeyDown}
                    icon={<LockIcon/>}
                    setValue={(input) => changeFormData("confirmPassword", input)}
                    onBlur={() => validateForm(formData)}
                    maxLength={20}
                    isError={errors?.confirmPassword?.value}
                    errorText={errors?.confirmPassword?.text}
                />
            </CreateAccountRow>

            <AppButtonAuth
                onClick={onSubmit}
                disabled={
                    checkEmailLoading || requestOTPLoading || isNextButtonDisabled
                }
            >
                Next
            </AppButtonAuth>
        </CreateAccountContainer>
    );
};

const CreateAccountContainer = styled.div`
    width: 100%;
    display: grid;
    gap: 0.8rem;
    font-family: var(--font-secondary);
    @media (width >= ${mediaSizes.md}) {
        padding: 0.5rem 1.5rem 0.5rem;
    }
`;

const CreateAccountRow = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.8rem;
`;

const TitleText = styled.h1`
    font-family: var(--font-primary);
    font-size: 19px;
    font-weight: 400;
    text-align: center;

    @media (width >= ${mediaSizes.md}) {
        font-size: 26px;
    }
`;

const SubtitleText = styled(HelperText)`
    margin-bottom: 1rem;
`;


